import type { ReactNode } from "react";

import { cn } from "../../lib/utils";
import { FILL_ROLES } from "../../lib/colors";
import { useSimpleChatMessageDirection } from "./useSimpleChatMessageDirection";

export interface SimpleChatMessageBubbleProps {
  className?: string;
  /** The message body, as plain text. */
  children?: ReactNode;
}

/**
 * One bubble within a `SimpleChatMessage`. Takes its tone and alignment from the message's
 * `direction`, so it is never passed down by the consumer.
 *
 * Padding lives here rather than on the message column so that sibling parts sharing the column can
 * set their own (an image or audio part wants none).
 */
export const SimpleChatMessageBubble = ({ className, children }: SimpleChatMessageBubbleProps) => {
  const direction = useSimpleChatMessageDirection();

  return (
    <div
      data-slot="simple-chat-message-bubble"
      className={cn(
        "rounded-2xl px-3.5 py-2 text-sm leading-5 whitespace-pre-wrap",
        // The outbound/inbound tones are the shared `primary` and `muted` surfaces, so they track
        // any change to those roles instead of drifting from Button's copy of the same pair.
        // `muted` writes in --foreground here (not the role's own muted ink) the same way Button's
        // solid muted does: this is a content surface, so it carries full-contrast body text.
        direction === "outbound" ? FILL_ROLES.primary.solidBase : "bg-muted text-foreground",
        className
      )}
    >
      {children}
    </div>
  );
};
